import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";
import { createResizeObserverLoopFilter } from "@/lib/telemetry/resizeObserverLoopFilter";
import { syncTelemetryUserContext } from "@/lib/telemetry/telemetryUserContext";
import { MANAGEMENT_API_HOST } from "@/services/appEnv";
import useUserTokenStore from "@/stores/user-token.store";

/** Dedicated instance for App Insights only — do not share with React Router (single-listener limit). */
const browserHistory = createBrowserHistory();
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
  config: {
    connectionString: import.meta.env.VITE_APP_INSIGHTS_CONNECTION_STRING,
    extensions: [reactPlugin],
    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
    // LAR-520: the API is on another origin (loda.dk → api.lara.law), and the SDK only sends
    // `traceparent`/`Request-Id` cross-origin when told to — without them a browser error cannot
    // be joined to the API request behind it. Limited to the API host: a header that Auth0,
    // Unleash, Graph, SignalR or blob storage do not allow in CORS fails the call's preflight.
    enableCorsCorrelation: true,
    correlationHeaderDomains: [MANAGEMENT_API_HOST],
    // LAR-627: off by default, so a promise nobody awaited — a fire-and-forget
    // handler, a `.then()` without a `.catch()` — failed without a trace.
    enableUnhandledPromiseRejectionTracking: true,
  },
});

appInsights.loadAppInsights();

// LAR-435: a layout oscillation emits this benign browser notice ~30x/second for as
// long as the tab is open. Keep one per page load, drop the rest.
appInsights.addTelemetryInitializer(createResizeObserverLoopFilter());

// LAR-520: tag every row with the signed-in Lara user and organization
// (`user_AuthenticatedId`, `user_AccountId`), following refreshes, organization
// switches and logout.
syncTelemetryUserContext(appInsights, useUserTokenStore);

export { reactPlugin, browserHistory, appInsights };
